import { Coupon } from '@dtx-company/true-common/src/types/Coupon'
import { Plan } from '@dtx-company/true-common/src/types/Plan'
import { PlanPurchaseFormData } from '../PlanPurchaseFormData'
import { PlanPurchaseFormPromo } from '@app/plans/src/PlanPurchaseForm/PlanPurchaseFormPromo'
import { UseFormSetValue } from 'react-hook-form'
import { getCoupon } from '@dtx-company/inter-app/src/api/subscription-service/getCoupon'
import { promoAppliesToSelectedPlan } from '@app/common/src/components/PlanPurchaseExperience/utils/promoAppliesToSelectedPlan'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { useMemo } from 'react'
import useSWR, { SWRResponse } from 'swr'
import useTranslation from 'next-translate/useTranslation'

type PromoCodeErrors = 'promo_code_wrong_plan_error' | 'promo_code_error'
type PromoCodeErrorMap = Record<PromoCodeErrors, string>

// Replacing this with useAPISWR caused the promocode to need to be submitted twice to get
// to the right state even though the code seemed functionally identical
// Leaving this here for now to be refactored later
export function useFetchPromoCodeSWR(couponCode: string | undefined): SWRResponse<Coupon | null> {
  return useSWR<Coupon | null>(
    couponCode ? ['useFetchPromoCodeSWR', couponCode] : null,
    ([_, couponCode]: [string, string]) => getCoupon({ code: couponCode }),
    { revalidateIfStale: false, revalidateOnFocus: false, revalidateOnMount: true }
  )
}

/**
 * This hook is used to validate a promo code
 * If a plan is passed in, it will also validate that the promo code applies to the selected plan
 * @param promoCode
 * @param plan - optional (set to validate the promo code for the selected plan)
 * @returns PlanPurchaseFormPromo
 */
export function usePlanPurchasePromoCode(
  promoCode: string | undefined,
  plan?: Plan,
  promoCodeErrorMap?: Partial<PromoCodeErrorMap>,
  setFormValue?: UseFormSetValue<PlanPurchaseFormData>
): PlanPurchaseFormPromo {
  const { t } = useTranslation('promocode')
  const { data, isLoading, isValidating } = useFetchPromoCodeSWR(promoCode)
  const isCouponCodeValid = Boolean(data)
  const isValidForSelectedPlan = data && plan ? promoAppliesToSelectedPlan(data, plan) : undefined
  const plans = useMemo(() => data?.planIds ?? [], [data?.planIds])
  const error = useMemo(() => {
    if (isLoading || promoCode === undefined) return undefined
    if (!isCouponCodeValid) {
      // remove any preselected promo code if it is no longer valid
      setFormValue?.('promoCode', undefined)
      const errorMessage =
        promoCodeErrorMap?.promo_code_error ?? t('error-messages.promo_code_error')
      sendErrorNotification(errorMessage)
      return errorMessage
    }
    if (plan && isValidForSelectedPlan === false) {
      // remove any preselected promo code if it is no longer valid
      setFormValue?.('promoCode', undefined)
      const errorMessage =
        promoCodeErrorMap?.promo_code_wrong_plan_error ??
        t('error-messages.promo_code_wrong_plan_error')
      sendErrorNotification(errorMessage)
      return errorMessage
    }
    return undefined
  }, [
    isLoading,
    promoCode,
    isCouponCodeValid,
    plan,
    isValidForSelectedPlan,
    setFormValue,
    promoCodeErrorMap?.promo_code_error,
    promoCodeErrorMap?.promo_code_wrong_plan_error,
    t
  ])

  return {
    code: promoCode ?? '', //this should be returned from the API in future iteration
    error,
    isLoading,
    isValid: isCouponCodeValid,
    isValidating,
    isValidForSelectedPlan,
    plans
  }
}
